import type {
    CreatureDerivedSpeedFoldInput,
    CreatureDerivedSpeedFoldResult,
    CreatureSpeedSource,
} from '../types';
import { applyCreatureSpeedFactorRows, creatureSpeedFactorRowsPresent } from './creature-speed-factor';

/** Fold one swimming/climbing speed through half-walk derivation and dynamic overrides. */
export function foldCreatureDerivedSpeedSources(input: CreatureDerivedSpeedFoldInput): CreatureDerivedSpeedFoldResult {
    const baseSources: CreatureSpeedSource[] = input.baseSources.map(source => ({ ...source }));
    const dynamicSources: CreatureSpeedSource[] = input.dynamicSources.map(source => ({ ...source }));
    const inventoryIndexes = dynamicSources
        .map((source, index) => source.kind === 'inventory_equipped' ? index : -1)
        .filter(index => index >= 0);
    if (inventoryIndexes.length > 1) {
        let winnerIndex = inventoryIndexes[0] ?? 0;
        for (const index of inventoryIndexes.slice(1)) {
            if ((dynamicSources[index]?.value ?? 0) > (dynamicSources[winnerIndex]?.value ?? 0)) winnerIndex = index;
        }
        for (const index of inventoryIndexes) {
            if (index === winnerIndex) continue;
            const source = dynamicSources[index];
            if (source) dynamicSources[index] = { ...source, raw_value: source.value, value: 0, suppressed: true };
        }
    }

    // A swim/climb carrying its own multiplier derives from the unfactored walk (see speeds.php).
    const halfBasis = creatureSpeedFactorRowsPresent(dynamicSources) ? (input.walkBasisPrefactor ?? input.walkBasis) : input.walkBasis;
    const halfWalk = halfBasis > 0 ? Math.round(halfBasis * 0.5) : 0;
    const base = baseSources.reduce((sum, source) => sum + source.value, 0);
    const dynamic = dynamicSources.reduce((sum, source) => sum + source.value, 0);
    const locked = input.dynamicBrace !== null;
    let total: number;
    let derived: boolean;
    let sources: CreatureSpeedSource[];
    if (input.defaultDerivation) {
        total = applyCreatureSpeedFactorRows(dynamicSources, input.dynamicBrace !== null ? Math.max(0, input.dynamicBrace) : halfWalk + dynamic, locked).total;
        derived = halfWalk !== 0;
        sources = dynamicSources;
    } else {
        const stacksOnDefault = halfWalk > 0 && base <= halfWalk;
        const effectiveBase = stacksOnDefault ? halfWalk + base : base;
        total = applyCreatureSpeedFactorRows(dynamicSources, input.dynamicBrace !== null ? Math.max(0, input.dynamicBrace) : effectiveBase + dynamic, locked).total;
        derived = input.dynamicBrace === null && stacksOnDefault;
        sources = [...baseSources, ...dynamicSources];
    }
    const categoryTotals = new Map<string, number>();
    for (const source of sources) {
        const category = typeof source.category === 'string' ? source.category : source.kind;
        categoryTotals.set(category, (categoryTotals.get(category) ?? 0) + source.value);
    }
    const breakdown = Object.fromEntries(categoryTotals);
    return {
        total,
        base: 0,
        locked: input.dynamicBrace !== null,
        ...(sources.length ? { sources } : {}),
        ...(Object.keys(breakdown).length ? { breakdown } : {}),
        ...(derived ? { derived: true as const } : {}),
    };
}
